import * as z from 'zod';
import { createSelectSchema } from 'drizzle-zod';

import { ABOUT_MAX_LENGTH, MEMBER_NAME_MAX_LENGTH, MEMBER_NAME_DENY_PATTERN } from '@iichi/domain/member/constants';
import type { QueryResult } from '@iichi/schema-database/types/member';
import * as schema from '@iichi/schema-database';

import { emailSchema } from '../lib/email';

const memberI18NSchema = createSelectSchema(schema.memberI18N)
  .pick({
    about: true,
    memberName: true,
  })
  .extend({
    about: z.string().max(ABOUT_MAX_LENGTH),
    memberName: z
      .string()
      .min(1)
      .max(MEMBER_NAME_MAX_LENGTH)
      .refine((value: string) => !MEMBER_NAME_DENY_PATTERN.test(value), '使用できないフレーズが含まれています'),
  });

export const memberSchema = createSelectSchema(schema.member)
  .pick({
    alias: true,
    createdAt: true,
    deleted: true,
    email: true,
    emailBounceCount: true,
    emailConfirmed: true,
    emailSuspended: true,
    likeCount: true,
    memberId: true,
    password: true,
    slug: true,
    updatedAt: true,
  })
  .extend({
    createdAt: z.coerce.date(),
    deleted: z.coerce.boolean(),
    // 型の調整
    email: emailSchema,
    emailBounceCount: z.coerce.number(),
    emailConfirmed: z.coerce.boolean(),
    emailSuspended: z.coerce.boolean(),
    likeCount: z.coerce.number(),
    password: z.hash('md5'),
    updatedAt: z.coerce.date(),
  })
  // プロパティを追加
  .extend({
    aliasOrSlug: z.string(),
  })
  // member_i18nのフィールドを追加
  .extend({
    about: memberI18NSchema.shape.about,
    memberName: memberI18NSchema.shape.memberName,
  });

export type Member = z.infer<typeof memberSchema>;

const serializeScehema = memberSchema.extend({
  email: z.string(),
  memberName: z.string(),
});

export const serialize = (data: QueryResult): Member => {
  const member = {
    about: data.memberI18Ns?.[0]?.about ?? '',
    alias: data.alias,
    aliasOrSlug: data.alias || data.slug,
    createdAt: data.createdAt,
    deleted: data.deleted,
    email: data.email,
    emailBounceCount: data.emailBounceCount,
    emailConfirmed: data.emailConfirmed,
    emailSuspended: data.emailSuspended,
    likeCount: data.likeCount,
    memberId: data.memberId,
    memberName: data.memberI18Ns?.[0]?.memberName ?? '',
    password: data.password,
    slug: data.slug,
    updatedAt: data.updatedAt,
  };

  // 出力時は緩くチェック
  return serializeScehema.parse(member);
};
